import { keepPreviousData, useQuery } from "@tanstack/react-query";
import { CampaignService } from "@gen/tracker/v1/campaign_connect";
import type {
  CampaignGroupTotal,
  CampaignListItem,
  CampaignOwner,
  CampaignQuickCounts,
} from "@gen/tracker/v1/campaign_pb";
import { DateRange, Pagination, SortOrder } from "@gen/tracker/v1/common_pb";
import { StatsSortOptions } from "@gen/tracker/v1/analytics_pb";
import type { StatsRow } from "@gen/tracker/v1/reporting_pb";

import { createClient } from "@/lib/connect";
import { campaignKeys } from "@/lib/services/campaign-service";
import type { DayRange } from "@/lib/period";

import {
  FOLD_GROUP_BY,
  QUICK_SERVER,
  metricFilters,
  statusesOf,
  type ListState,
} from "./list-url";

const client = createClient(CampaignService);

/** The rows on screen at once: the server counts every campaign for any
 * page, so «Итого», the folds and the quick views stay the whole list's. */
export const LIST_PAGE = 250;
/** The whole list for CSV: one answer of up to 20000 rows (the server's ceiling). */
const EXPORT_PAGE = 20000;

export interface CampaignListQuery {
  state: ListState;
  range: DayRange;
  compare: DayRange | undefined;
  tz: string;
  /** «Мои» of the Top bar: the caller's sub; it narrows the «Баер» filter. */
  mine: string | undefined;
  sort: { key: string; desc: boolean };
  isPercent: (metric: string) => boolean;
}

export interface CampaignListData {
  items: CampaignListItem[];
  totals: StatsRow | undefined;
  compareTotals: StatsRow | undefined;
  groupTotals: CampaignGroupTotal[];
  quickCounts: CampaignQuickCounts | undefined;
  total: number;
}

/** The request of one page of the list — everything the server needs to decide which rows and which numbers. Pure. */
export function listRequest(q: CampaignListQuery, offset: number, limit: number) {
  const { state } = q;
  const owners = q.mine ? [q.mine] : (state.facets.owner ?? []);
  return {
    pagination: new Pagination({ limit, offset }),
    statuses: statusesOf(state),
    search: state.search.trim(),
    ownerIds: owners,
    trafficSourceIds: state.facets.source ?? [],
    groupIds: state.facets.group ?? [],
    countries: state.facets.geo ?? [],
    offerIds: state.facets.offer ?? [],
    metricFilters: metricFilters(state.conditions, q.isPercent),
    quick: QUICK_SERVER[state.quick] ?? "",
    groupBy: FOLD_GROUP_BY[state.fold],
    statsRange: new DateRange({ from: q.range.from, to: q.range.to, timezone: q.tz }),
    compareRange: q.compare ? new DateRange({ from: q.compare.from, to: q.compare.to, timezone: q.tz }) : undefined,
    sort: new StatsSortOptions({
      metricKey: q.sort.key,
      order: q.sort.desc ? SortOrder.DESC : SortOrder.ASC,
    }),
  };
}

/**
 * One page of the list as the server counted it: `LIST_PAGE` campaigns of
 * the sorted list with their numbers and flags, and over all of them
 * «Итого», the folds' header lines and the quick views' counts. The
 * previous answer stays on screen while the next is on its way and when it
 * fails (board CampError).
 */
export function useCampaignList(q: CampaignListQuery, pageIndex: number, enabled = true) {
  const key = { ...q, isPercent: undefined, filters: metricFilters(q.state.conditions, q.isPercent).map((f) => f.toJsonString()) };
  return useQuery({
    queryKey: [...campaignKeys.all, "screen", key, pageIndex] as const,
    enabled,
    placeholderData: keepPreviousData,
    staleTime: 30_000,
    // The morning screen stays current on its own; a refresh that fails
    // keeps the last numbers and says so (board CampError).
    refetchInterval: 60_000,
    queryFn: async (): Promise<CampaignListData> => {
      const res = await client.listCampaigns(listRequest(q, pageIndex * LIST_PAGE, LIST_PAGE));
      return {
        items: res.items,
        totals: res.totals,
        compareTotals: res.compareTotals,
        groupTotals: res.groupTotals,
        quickCounts: res.quickCounts,
        total: res.pagination?.total ?? res.items.length,
      };
    },
  });
}

/**
 * Every campaign of the list, for CSV. Past `EXPORT_PAGE` the other pages
 * are read at once — the server counted the whole list for the first.
 */
export async function fetchCampaignListAll(q: CampaignListQuery): Promise<CampaignListItem[]> {
  const first = await client.listCampaigns(listRequest(q, 0, EXPORT_PAGE));
  const total = first.pagination?.total ?? first.items.length;
  const offsets: number[] = [];
  for (let offset = first.items.length; offset > 0 && offset < total; offset += EXPORT_PAGE) offsets.push(offset);
  const rest = await Promise.all(offsets.map((offset) => client.listCampaigns(listRequest(q, offset, EXPORT_PAGE))));
  return [...first.items, ...rest.flatMap((page) => page.items)];
}

/**
 * How many campaigns a draft of the metric conditions would leave
 * (board CampFilter «Найдено 3 кампании») — the server's count, one row asked for.
 */
export function useCampaignCount(q: CampaignListQuery, enabled: boolean) {
  const key = { ...q, isPercent: undefined, filters: metricFilters(q.state.conditions, q.isPercent).map((f) => f.toJsonString()) };
  return useQuery({
    queryKey: [...campaignKeys.all, "screen-count", key] as const,
    enabled,
    placeholderData: keepPreviousData,
    queryFn: async () => {
      const res = await client.listCampaigns({ ...listRequest(q, 0, 1), groupBy: "" });
      return res.pagination?.total ?? 0;
    },
  });
}

/** The people who own campaigns — the «Баер» filter's options. */
export function useCampaignOwners() {
  return useQuery({
    queryKey: [...campaignKeys.all, "owners"] as const,
    staleTime: 5 * 60_000,
    queryFn: async (): Promise<CampaignOwner[]> => (await client.listCampaignOwners({})).owners,
  });
}
